import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { fromDateInput, toDateInput, useAppStore } from '@restorant/core';
import type { Expense } from '@restorant/core';
import { useColors } from '../theme';
import { useT } from '../i18n';
import { useExpenseCategories, useExpenseHeadLabel } from '../menu';
import type { IconName } from '../icons/Icon';
import { Chip } from './Chip';
import { Sheet, SheetButton } from './Sheet';
import { T } from './Text';
import { TextField } from './TextField';

/**
 * Record or amend one payment out.
 *
 * Writes straight to the store, which queues it for upload like every other
 * change — so an owner standing in the vegetable market with no signal can
 * still record what they just handed over.
 */
export function ExpenseEditor({
  expense,
  visible,
  onClose,
}: {
  /** null = recording a new one. */
  expense: Expense | null;
  visible: boolean;
  onClose: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const heads = useExpenseCategories();
  const headLabel = useExpenseHeadLabel();
  const add = useAppStore((s) => s.addExpense);
  const update = useAppStore((s) => s.updateExpense);
  const remove = useAppStore((s) => s.deleteExpense);

  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [payee, setPayee] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setAmount(expense ? String(expense.amount) : '');
    // A new entry defaults to the first head rather than to nothing: most
    // payments are the same two or three heads, and one fewer tap matters when
    // this is being filled in standing up.
    setCategoryId(expense ? expense.categoryId : (heads[0]?.id ?? null));
    setPayee(expense?.payee ?? '');
    setNote(expense?.note ?? '');
    // Today, because an expense is nearly always recorded the day it happens —
    // and when it is not, the field is right there to correct.
    setDate(toDateInput(expense ? new Date(expense.paidAt) : new Date()));
    setError(null);
    setConfirmDelete(false);
    // `heads` is deliberately not a dependency: re-running this when the list
    // reloads mid-edit would throw away what has been typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense, visible]);

  const save = () => {
    const value = parseFloat(amount);
    if (!Number.isFinite(value) || value <= 0) return setError(t('expense_amount_required'));
    if (!categoryId) return setError(t('expense_head_required'));

    const day = fromDateInput(date);
    if (!day) return setError(t('expense_date_required'));

    // Midday, not midnight. The entry carries a date but no clock time, and a
    // midnight stamp lands on a bucket boundary — an hour of clock skew, or a
    // daylight-saving shift, would drop yesterday's rent into the day before.
    day.setHours(12, 0, 0, 0);

    if (expense) {
      update({ ...expense, categoryId, amount: value, payee, note, paidAt: day.getTime() });
    } else {
      add({ categoryId, amount: value, payee, note, paidAt: day.getTime() });
    }
    onClose();
  };

  const destroy = () => {
    if (!expense) return;
    if (!confirmDelete) return setConfirmDelete(true);
    remove(expense.id);
    onClose();
  };

  return (
    <Sheet
      visible={visible}
      title={expense ? t('edit_expense') : t('add_expense')}
      onClose={onClose}
      footer={
        <>
          {expense ? (
            <SheetButton
              label={confirmDelete ? t('delete_expense_confirm') : t('delete')}
              icon="trash"
              tone="danger"
              onPress={destroy}
            />
          ) : null}
          <SheetButton label={t('save')} icon="check" tone="primary" onPress={save} />
        </>
      }
    >
      <TextField
        testID="expense-amount"
        label={t('expense_amount')}
        icon="rupee"
        value={amount}
        // A decimal point is allowed — 42.50 for a bill is normal — but nothing
        // else, so the parse below cannot be handed something unusable.
        onChangeText={(v) => setAmount(v.replace(/[^0-9.]/g, ''))}
        keyboardType="decimal-pad"
        placeholder="0"
      />

      <View style={{ gap: 8 }}>
        <T size={11.5} weight={600} color={colors.textFaint}>
          {t('expense_head')}
        </T>
        {heads.length ? (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {heads.map((h) => (
              <Chip
                key={h.id}
                // The translated name, not h.name. This picker is used daily,
                // by an owner who may be running the whole till in Gujarati —
                // the canonical English name belongs in the head editor, where
                // it is the field being edited, not here.
                label={headLabel(h.id)}
                icon={h.icon as IconName}
                active={categoryId === h.id}
                onPress={() => setCategoryId(h.id)}
              />
            ))}
          </View>
        ) : (
          <T size={12} color={colors.textFaint}>
            {t('no_heads_yet')}
          </T>
        )}
      </View>

      <View style={{ gap: 6 }}>
        <TextField
          testID="expense-date"
          label={t('expense_date')}
          icon="calendar"
          value={date}
          onChangeText={setDate}
          placeholder={t('date_hint')}
        />
        <T size={11.5} color={colors.textFaint}>
          {t('expense_date_hint')}
        </T>
      </View>

      <View style={{ gap: 6 }}>
        <TextField
          testID="expense-payee"
          label={t('expense_payee')}
          icon="user"
          value={payee}
          onChangeText={setPayee}
        />
        <T size={11.5} color={colors.textFaint}>
          {t('expense_payee_hint')}
        </T>
      </View>

      <TextField
        testID="expense-note"
        label={t('expense_note')}
        value={note}
        onChangeText={setNote}
      />

      {error ? (
        <T size={12} color={colors.spice}>
          {error}
        </T>
      ) : null}
    </Sheet>
  );
}
